import { ComponentProps, Ref, useCallback, useMemo, useRef } from 'react';
import tinycolor from 'tinycolor2';

import styles from '~/modules/game-engine/drawing/styles';
import { usePlayerMicData } from '~/modules/hooks/players/use-player-mic';
import { ONLINE_STATS_PUBLISH_MS } from '~/modules/online/protocol/consts';
import { PlayerNumber } from '~/modules/players/player-number';
import { twx } from '~/utils/twx';

const usePlayerColor = (playerNumber: PlayerNumber) => {
  return useMemo(() => {
    if (!styles.colors.players[playerNumber]) return '0, 0, 0';
    const rgb = tinycolor(styles.colors.players[playerNumber].text).toRgb();
    return `${rgb.r}, ${rgb.g}, ${rgb.b}`;
  }, [playerNumber]);
};

const VolumeIndicatorBase = twx.div`pointer-events-none absolute top-0 right-0 z-1 h-full w-full origin-right overflow-hidden bg-repeat-y`;

interface Props extends ComponentProps<typeof VolumeIndicatorBase> {
  playerNumber: PlayerNumber;
  volume: number;
  /**
   * How long the bar takes to travel to a new level. Match it to how often levels actually arrive:
   * the default is the online publish interval, and the remote mic passes its own throttle instead.
   */
  transitionMs?: number;
  ref?: Ref<HTMLDivElement | null>;
}

/**
 * Volume bar driven by an explicitly supplied level, for singers whose audio isn't read on this
 * device through `usePlayerMicData` — online rooms report their own volume, and the remote mic reads
 * its own hardware. For a host-side local mic use `PlayerMicCheck` instead.
 */
export const VolumeIndicator = ({
  volume,
  playerNumber,
  transitionMs = ONLINE_STATS_PUBLISH_MS,
  ref,
  ...rest
}: Props) => {
  const percent = `${Math.min(1, volume * 20)}`;
  const color = usePlayerColor(playerNumber);

  return (
    // Same two-element shape as PlayerMicCheck below, and for the same reason: the scale has to sit
    // on an inner element. Scaling the rounded, clipping box itself squashes its corner radius along
    // with it, and the bar then bleeds past the corners of the row it's sitting in.
    <VolumeIndicatorBase {...rest} data-test="mic-volume-indicator" ref={ref}>
      <div
        className="h-full w-full origin-right"
        style={{
          transform: `scaleX(${percent})`,
          // Reported levels land a few times a second, which without this reads as a bar hopping
          // between positions — a linear tween over one reporting interval turns it into a meter
          transition: `transform ${transitionMs}ms linear`,
          background: `linear-gradient(270deg, rgba(${color}, 1) 0%, rgba(${color}, 0) 100%)`,
        }}
      />
    </VolumeIndicatorBase>
  );
};

export const PlayerMicCheck = ({
  playerNumber,
  ...props
}: { playerNumber: PlayerNumber } & ComponentProps<typeof VolumeIndicatorBase>) => {
  const elemRef = useRef<HTMLDivElement | null>(null);

  const cb = useCallback(([volume]: [number, number]) => {
    if (elemRef.current) {
      const percent = `${Math.round(Math.min(1, volume * 20) * 100) / 100}`;

      elemRef.current.style.transform = `scaleX(${percent})`;
    }
  }, []);

  usePlayerMicData(playerNumber, cb);
  const color = usePlayerColor(playerNumber);

  return (
    <VolumeIndicatorBase {...props} data-test="mic-volume-indicator">
      <div
        className="h-full w-full origin-right"
        ref={elemRef}
        style={{
          background: `linear-gradient(270deg, rgba(${color}, 1) 0%, rgba(${color}, 0) 100%)`,
        }}
      />
    </VolumeIndicatorBase>
  );
};
